/**
 * /billing/school data layer (Plan 14).
 *
 * REST surface served by `apps/ordo/server/src/routes/billing-school.ts`
 * — TanStack Query is the only access pattern in the client per the
 * apps/ordo policy split. See `apps/ordo/CLAUDE.md` § "API style".
 *
 * Hooks:
 *   - useSchoolARSummaryQuery(filter) — page payload (permittedSchools,
 *     aging, invoices) for the SchoolARPage
 *   - useSchoolARInvoiceDetailQuery(invoiceId) — drawer payload
 *   - usePayByCheckMutation() — POST /pay-by-check (records a mailed check)
 *   - usePayNowMutation() — POST /pay-now (returns the Stripe-hosted invoice URL)
 *
 * Types mirror the server-side `SchoolARPagePayload` /
 * `SchoolARInvoiceRow` shapes so the components can import directly
 * from here.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Types — kept structurally identical to the server-side response shape
// ---------------------------------------------------------------------------

export type ArAgingBucket = 'current' | 'days0to30' | 'days31to60' | 'days60Plus'

export type MealPlanInvoiceStatus =
  | 'DRAFT'
  | 'PENDING_REVIEW'
  | 'APPROVED'
  | 'SCHEDULED'
  | 'ISSUED'
  | 'COLLECTED'
  | 'PAYMENT_FAILED'
  | 'VOIDED'
export type MealPlanInvoiceCheckPaymentStatus = 'SUBMITTED' | 'CONFIRMED' | 'CANCELED'

export interface SchoolARCheckPaymentSummary {
  id: string
  status: MealPlanInvoiceCheckPaymentStatus
  /** CHECK | ACH | WIRE | CASH | OTHER. */
  method: string
  checkNumber: string
  submittedAmountCents: number
  confirmedAmountCents: number | null
  currency: string
  mailedAt: string
  receivedAt: string | null
  memo: string | null
  depositRef: string | null
  photoS3Key: string | null
  submittedByUserId: string | null
  confirmedByUserId: string | null
  canceledByUserId: string | null
  confirmedAt: string | null
  canceledAt: string | null
  cancelReason: string | null
}

export interface SchoolARArAging {
  current: number
  days0to30: number
  days31to60: number
  days60Plus: number
  currency: string
  counts: {
    current: number
    days0to30: number
    days31to60: number
    days60Plus: number
  }
}

export interface SchoolARPermittedSchool {
  locationId: string
  locationName: string
  organizationId: string
  organizationName: string
}

export interface SchoolARInvoiceRow {
  id: string
  /** Null for account-owned invoices (org-AR, software fees). */
  mealPlanId: string | null
  groupId: string | null
  status: MealPlanInvoiceStatus
  periodStart: string
  periodEnd: string
  dueAt: string
  scheduledSendAt: string | null
  issuedAt: string | null
  sentAt: string | null
  totalCents: number
  paidCents: number
  currency: string
  organizationId: string
  organizationName: string
  schoolLocationId: string | null
  schoolLocationName: string | null
  bucket: ArAgingBucket
  stripeInvoiceRef: string | null
  stripeHostedInvoiceUrl: string | null
  stripeInvoicePdfUrl: string | null
  stripeInvoiceNumber: string | null
  payByTaskId: string | null
  checkPayment: SchoolARCheckPaymentSummary | null
  paymentState: InvoicePaymentState
  /**
   * True for a district (org-level) invoice the viewer sees only as a Campus
   * Admin. The page labels it as the district's and hides its row actions;
   * the server refuses its detail, documents and payment (PROD-1467).
   * Optional so a row built without it (an older server, a test fixture)
   * reads as an ordinary invoice.
   */
  districtInvoiceForCampusAdmin?: boolean
}

/** Mirrors `resolveInvoicePaymentState` in the server manager. */
export type InvoicePaymentState =
  | 'VOIDED'
  | 'PAID'
  | 'PAID_BY_CHECK'
  | 'CHECK_SUBMITTED'
  | 'PAYMENT_FAILED'
  | 'PAST_DUE'
  | 'DUE'
  | 'NOT_YET_ISSUED'

export interface SchoolARPagePayload {
  permittedSchools: SchoolARPermittedSchool[]
  aging: SchoolARArAging
  invoices: SchoolARInvoiceRow[]
  currency: string
}

/**
 * One node of the summary rollup: a mealtime, a meal plan beneath it, or an
 * eligibility tier beneath that. The tier level is present only where a plan
 * has more than one tier to tell apart.
 */
export interface SchoolARInvoiceRollupNode {
  key: string
  label: string
  amountCents: number
  quantity: number
  /**
   * Per-unit price every counted line beneath the node shares, or null when
   * they differ — leaves are split per rate, so a null always has its detail
   * one level down. Mirrors `unitPriceCents` in `@ordo/schema`.
   */
  unitPriceCents: number | null
  children: SchoolARInvoiceRollupNode[]
}

/**
 * Mirrors `InvoiceDocument` in `@ordo/schema`: the Location → Mealtime →
 * Meal Plan → Eligibility rollup of the invoice's frozen lines plus the totals
 * block — exactly what the summary PDF prints, so the drawer and the download
 * never disagree.
 */
export interface SchoolARInvoiceDocument {
  campuses: Array<{
    campusId: string | null
    campusName: string
    amountCents: number
    quantity: number
    /** See `SchoolARInvoiceRollupNode.unitPriceCents`. */
    unitPriceCents: number | null
    taxCents: number
    /** Blended rate in the shared 0.001% storage unit; null when untaxed. */
    taxRateBps: number | null
    mealtimes: SchoolARInvoiceRollupNode[]
  }>
  totals: {
    subtotalCents: number
    taxCents: number
    discountCents: number
    /** Positive non-item money, one row per type ("Software fees", "Work orders", "Adjustments"), in display order. */
    charges: Array<{ key: string; label: string; amountCents: number }>
    /** Everything that reduced the bill beyond item discounts, as a positive magnitude. */
    deductionsCents: number
    totalCents: number
    quantity: number
    /**
     * Full value of the meals served in the period, whoever paid for it.
     * INFORMATIONAL — outside the Total arithmetic, and rendered as a memo
     * row below it.
     */
    grossCents: number
    /**
     * What families paid directly toward those same meals. This is what
     * makes a parent-order school's bill legible: without it the school
     * reads its own residual as the whole story. INFORMATIONAL, as above.
     */
    familyPaidCents: number
  }
  /** Informational: the rollup always shows three levels, a plan-less line under its order route. */
  hasMealPlans: boolean
  currency: string
}

export interface SchoolARInvoiceDetail {
  invoice: SchoolARInvoiceRow
  document: SchoolARInvoiceDocument
  creditNotes: Array<{
    id: string
    amountCents: number
    currency: string
    reason: string
    status: string
    createdAt: string
    appliedAt: string | null
  }>
  checkPayments: SchoolARCheckPaymentSummary[]
  stripeHostedUrl: string | null
  stripeInvoicePdfUrl: string | null
}

export interface SchoolARFilter {
  /** Empty / null => "All schools" view (org-level default). */
  schoolLocationIds?: string[] | null
}

export interface PayByCheckInput {
  invoiceId: string
  checkNumber: string
  mailedAt: string
  amountCents: number
  photoS3Key?: string | null
  memo?: string | null
}

// ---------------------------------------------------------------------------
// URL builders
// ---------------------------------------------------------------------------

function summaryQuery(filter: SchoolARFilter): Record<string, string | string[] | undefined> {
  const ids = filter.schoolLocationIds ?? null
  if (!ids || ids.length === 0) {
    return {}
  }
  return { schoolLocationId: ids }
}

function urlWithMultiQuery(path: string, query: Record<string, string | string[] | undefined>): string {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) {
      continue
    }
    if (Array.isArray(v)) {
      for (const item of v) {
        params.append(k, item)
      }
    } else {
      params.set(k, v)
    }
  }
  const qs = params.toString()
  return qs.length === 0 ? path : `${path}?${qs}`
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

export async function fetchSchoolARSummary(filter: SchoolARFilter, signal?: AbortSignal): Promise<SchoolARPagePayload> {
  const url = urlWithMultiQuery('/api/billing/school/summary', summaryQuery(filter))
  return apiFetch<SchoolARPagePayload>(url, { signal })
}

export async function fetchSchoolARInvoiceDetail(
  invoiceId: string,
  signal?: AbortSignal,
): Promise<SchoolARInvoiceDetail> {
  return apiFetch<SchoolARInvoiceDetail>(`/api/billing/school/invoices/${encodeURIComponent(invoiceId)}`, { signal })
}

export async function submitPayByCheck(input: PayByCheckInput): Promise<{ checkPayment: SchoolARCheckPaymentSummary }> {
  const { invoiceId, ...body } = input
  return apiFetch<{ checkPayment: SchoolARCheckPaymentSummary }>(
    `/api/billing/school/invoices/${encodeURIComponent(invoiceId)}/pay-by-check`,
    {
      method: 'POST',
      body,
    },
  )
}

export async function cancelPayByCheck(checkPaymentId: string): Promise<{ checkPayment: SchoolARCheckPaymentSummary }> {
  return apiFetch<{ checkPayment: SchoolARCheckPaymentSummary }>(
    `/api/billing/school/payment-attempts/${encodeURIComponent(checkPaymentId)}`,
    {
      method: 'DELETE',
    },
  )
}

export async function startPayNow(invoiceId: string): Promise<{ stripeHostedUrl?: string }> {
  return apiFetch<{ stripeHostedUrl?: string }>(
    `/api/billing/school/invoices/${encodeURIComponent(invoiceId)}/pay-now`,
    { method: 'POST', body: {} },
  )
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const schoolARSummaryQueryKey = (filter: SchoolARFilter) =>
  ['billing', 'school', 'summary', { schoolLocationIds: filter.schoolLocationIds ?? null }] as const

export function useSchoolARSummaryQuery(filter: SchoolARFilter = {}) {
  return useQuery<SchoolARPagePayload>({
    queryKey: schoolARSummaryQueryKey(filter),
    queryFn: ({ signal }) => fetchSchoolARSummary(filter, signal),
    staleTime: 30_000,
  })
}

export const schoolARInvoiceDetailKey = (invoiceId: string | null) =>
  ['billing', 'school', 'invoice', invoiceId] as const

export function useSchoolARInvoiceDetailQuery(invoiceId: string | null) {
  return useQuery<SchoolARInvoiceDetail>({
    queryKey: schoolARInvoiceDetailKey(invoiceId),
    queryFn: ({ signal }) => {
      if (!invoiceId) {
        throw new Error('useSchoolARInvoiceDetailQuery: invoiceId is required')
      }
      return fetchSchoolARInvoiceDetail(invoiceId, signal)
    },
    enabled: Boolean(invoiceId),
    staleTime: 30_000,
  })
}

export function usePayByCheckMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: submitPayByCheck,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing', 'school'] })
    },
  })
}

export function useCancelPayByCheckMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: cancelPayByCheck,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing', 'school'] })
    },
  })
}

export function usePayNowMutation() {
  return useMutation({ mutationFn: startPayNow })
}

// ---------------------------------------------------------------------------
// Invoice artifacts — the two files a school reconciles against
// ---------------------------------------------------------------------------

/**
 * Item-manifest CSV. A plain authenticated GET the browser downloads —
 * no fetch/blob dance, so a 8,000-row district manifest streams straight
 * to disk instead of through JS memory.
 */
export function invoiceManifestCsvUrl(invoiceId: string): string {
  return `/api/billing/school/invoices/${encodeURIComponent(invoiceId)}/item-manifest.csv`
}

/**
 * Summary PDF. An authenticated GET that answers the PDF BYTES with the
 * legacy filename in `content-disposition` — or JSON: 202 while the render
 * is still running, 409 when the invoice's lines do not reconcile, 503 when
 * rendering is not configured. Fetch it through `fetchAuthedPdf`, never a
 * bare link: a bare link would save that JSON under a `.pdf` name.
 */
export function invoiceSummaryPdfUrl(invoiceId: string): string {
  return `/api/billing/school/invoices/${encodeURIComponent(invoiceId)}/summary.pdf`
}

// ---------------------------------------------------------------------------
// Order reconciliation — the range report (PROD-1378)
// ---------------------------------------------------------------------------

export interface OrderReconciliationFilter {
  /** ISO `YYYY-MM-DD`, inclusive. */
  from: string
  to: string
  /** Null = every campus the admin can see. */
  schoolLocationId?: string | null
  /** Null = every mealtime. */
  mealtime?: string | null
}

export interface OrderReconciliationSummary {
  orderCount: number
  itemCount: number
  totalCents: number
  currency: string
}

export interface OrderReconciliationRow {
  orderItemId: string
  orderBatchId: string
  campus: string
  serviceDate: string
  mealtime: string
  description: string
  quantity: number
  unitPriceCents: number | null
  amountCents: number
  currency: string
}

export interface OrderReconciliationPayload {
  rows: OrderReconciliationRow[]
  summary: OrderReconciliationSummary
}

function orderReconciliationSearch(filter: OrderReconciliationFilter): string {
  const params = new URLSearchParams({ from: filter.from, to: filter.to })
  if (filter.schoolLocationId) {
    params.set('schoolLocationId', filter.schoolLocationId)
  }
  if (filter.mealtime) {
    params.set('mealtime', filter.mealtime)
  }
  return params.toString()
}

/**
 * CSV form of the same report. Fetch it through `fetchAuthedCsv`, never a
 * bare link — the impersonation + kiosk headers live in storage, not in a
 * cookie, so a bare navigation would resolve as the wrong actor and paint
 * a JSON error body over the app.
 */
export function orderReconciliationCsvUrl(filter: OrderReconciliationFilter): string {
  return `/api/billing/school/order-reconciliation.csv?${orderReconciliationSearch(filter)}`
}

export const orderReconciliationQueryKey = (filter: OrderReconciliationFilter) =>
  [
    'billing',
    'school',
    'order-reconciliation',
    filter.from,
    filter.to,
    filter.schoolLocationId ?? null,
    filter.mealtime ?? null,
  ] as const

/**
 * The on-screen preview behind the download — the count + total a school
 * reads before it exports. `staleTime: 0`: an export must read the orders
 * as they are NOW, matching the student-orders export.
 */
export function useOrderReconciliationQuery(filter: OrderReconciliationFilter | null) {
  return useQuery({
    queryKey: filter ? orderReconciliationQueryKey(filter) : ['billing', 'school', 'order-reconciliation', 'idle'],
    enabled: filter !== null,
    staleTime: 0,
    queryFn: () =>
      apiFetch<OrderReconciliationPayload>(
        `/api/billing/school/order-reconciliation?${orderReconciliationSearch(filter as OrderReconciliationFilter)}`,
      ),
  })
}
